// src/Admin/CrossSellAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listFornitori, me } from "../Admin/adminApi";
import "./CrossSellAdmin.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ----------------------------- utils ----------------------------- */
const headersAuth = (token) =>
  token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };

async function api(method, path, body, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: headersAuth(token),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(msg || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json().catch(() => ({}));
}

function isEmpty(v) {
  return v === undefined || v === null || v === "";
}
function clampPercent(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, n));
}

function itemTitle(it) {
  return (
    it.custom_title ||
    it.titolo ||
    it.display_title ||
    (it.service_id ? `Service #${it.service_id}` : "—")
  );
}

/* =================================================================
   CrossSellAdmin – Set + Items (link) + Libreria items
   - Detach dal set
   - Soft delete/restaure globali (opzionale ma utile)
   - Reorder via /sets/:id/items/reorder
   ================================================================= */
export default function CrossSellAdmin() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  /* -------------------- guard: solo admin -------------------- */
  const [role, setRole] = useState("guest");
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const u = await me(token);
        if (ok) setRole(u?.role || "guest");
      } catch {
        if (ok) setRole("guest");
      }
    })();
    return () => {
      ok = false;
    };
  }, [token]);
  const isAdmin = role === "admin";

  /* -------------------- fornitori per select -------------------- */
  const [fornitori, setFornitori] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        if (alive) setFornitori([]);
        return;
      }
      try {
        const rows = await listFornitori(token);
        if (alive) setFornitori(Array.isArray(rows) ? rows : []);
      } catch {
        if (alive) setFornitori([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const supplierNameById = useMemo(() => {
    const m = new Map();
    fornitori.forEach((f) =>
      m.set(String(f.id), f.nome || `Fornitore #${f.id}`)
    );
    return m;
  }, [fornitori]);

  /* -------------------- sets per sezione -------------------- */
  const [setsChips, setSetsChips] = useState([]);
  const [setsCarousel, setSetsCarousel] = useState([]);
  const [loadingChips, setLoadingChips] = useState(false);
  const [loadingCarousel, setLoadingCarousel] = useState(false);
  const [errorChips, setErrorChips] = useState("");
  const [errorCarousel, setErrorCarousel] = useState("");

  // meta items preview: { [setId]: { count, first3: [t1,t2,t3], full } }
  const [itemsMetaMap, setItemsMetaMap] = useState({});

  // toggle: mostra archiviati in tab Items (inteso: deleted_at global item)
  const [showDeleted, setShowDeleted] = useState(false);

  const sortByOrder = useCallback(
    (arr) =>
      arr.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    []
  );

  const loadSetsFor = useCallback(
    async (placement) => {
      const setLoading =
        placement === "chips" ? setLoadingChips : setLoadingCarousel;
      const setError = placement === "chips" ? setErrorChips : setErrorCarousel;
      const setData = placement === "chips" ? setSetsChips : setSetsCarousel;

      setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams({ placement });
        const data = await api(
          "GET",
          `/api/admin/cross-sell/sets?${qs.toString()}`,
          null,
          token
        );
        setData(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e.message || "Errore nel caricamento dei set");
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  const loadAll = useCallback(async () => {
    await Promise.all([loadSetsFor("chips"), loadSetsFor("carousel")]);
  }, [loadSetsFor]);

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin, loadAll]);

  // preview items per i set in elenco (primi 3) -> SOLO VIVI
  useEffect(() => {
    const allSets = [...setsChips, ...setsCarousel];
    if (allSets.length === 0) return;
    let cancelled = false;

    (async () => {
      try {
        const entries = await Promise.all(
          allSets.map(async (s) => {
            try {
              const items = await api(
                "GET",
                `/api/admin/cross-sell/sets/${s.id}/items`,
                null,
                token
              );
              const arr = Array.isArray(items) ? items : [];
              const titles = arr
                .filter((it) => !it.deleted_at)
                .map((it) => itemTitle(it))
                .filter(Boolean);

              const first3 = titles.slice(0, 3);
              const full = titles.join(", ");
              return [s.id, { count: titles.length, first3, full }];
            } catch {
              return [s.id, { count: 0, first3: [], full: "" }];
            }
          })
        );

        if (!cancelled) {
          const next = {};
          entries.forEach(([id, meta]) => (next[id] = meta));
          setItemsMetaMap((prev) => ({ ...prev, ...next }));
        }
      } catch {
        // niente
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setsChips, setsCarousel, token]);

  /* -------------------- Toasts -------------------- */
  const [toasts, setToasts] = useState([]);
  const dismissToast = useCallback((id) => {
    setToasts((prev) => {
      const t = prev.find((x) => x.id === id);
      if (t?.timeoutId) clearTimeout(t.timeoutId);
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  const showToast = useCallback(
    ({
      type = "info",
      message = "",
      actionLabel,
      onAction,
      autoClose = true,
      duration = 4000,
    }) => {
      const id = Math.random().toString(36).slice(2);
      const timeoutId =
        autoClose &&
        setTimeout(() => {
          dismissToast(id);
        }, duration);

      setToasts((prev) => [
        ...prev,
        { id, type, message, actionLabel, onAction, autoClose, timeoutId },
      ]);
      return id;
    },
    [dismissToast]
  );

  const confirmToast = useCallback(
    (
      message,
      onConfirm,
      { type = "warning", confirmLabel = "Conferma", duration = 8000 } = {}
    ) => {
      const id = showToast({
        type,
        message,
        actionLabel: confirmLabel,
        onAction: () => {
          dismissToast(id);
          onConfirm?.();
        },
        autoClose: true,
        duration,
      });
      return id;
    },
    [showToast, dismissToast]
  );

  /* -------------------- Body scroll lock quando modale aperta -------------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [itemFormOpen, setItemFormOpen] = useState(false);
  useEffect(() => {
    const open = modalOpen || itemFormOpen;
    if (open) document.body.classList.add("cs-no-scroll");
    else document.body.classList.remove("cs-no-scroll");
    return () => document.body.classList.remove("cs-no-scroll");
  }, [modalOpen, itemFormOpen]);

  /* -------------------- Modale Set (tab Dettagli/Items) -------------------- */
  const [modalTab, setModalTab] = useState("details"); // 'details' | 'items'
  const [createdThisSession, setCreatedThisSession] = useState(false);
  // Item Hub UI
  const [itemHubTab, setItemHubTab] = useState("library"); // 'create' | 'library'


  const [modalSet, setModalSet] = useState({
    id: null,
    placement: "chips",
    titolo: "",
    descrizione: "",
    active: true,
    sort_order: 0,
    fornitoriIds: [],
  });
  const editingSet = !!modalSet.id;

  // items associati al set
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // libreria items (globale)
  const [libSearch, setLibSearch] = useState("");
  const [libItems, setLibItems] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libIncludeDeleted, setLibIncludeDeleted] = useState(false);

  // create item (globale + attach)
  const [newItem, setNewItem] = useState({
    custom_title: "",
    custom_price: "",
    share_percent: "",
    custom_image: "",
    custom_badge: "",
    custom_note: "",
  });
  const [showMoreItemFields, setShowMoreItemFields] = useState(false);

  const ensureSetExists = useCallback(async () => {
    if (modalSet.id) return modalSet.id;

    const titoloVal = (modalSet.titolo || "").trim() || "Senza titolo";
    const ids = (modalSet.fornitoriIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);

    const payload = {
      placement: modalSet.placement,
      titolo: titoloVal,
      descrizione: (modalSet.descrizione || "").trim(),
      active: !!modalSet.active,
      sort_order: Number(modalSet.sort_order || 0),
      fornitore_ids: ids,
    };

    const created = await api("POST", `/api/admin/cross-sell/sets`, payload, token);
    setModalSet((s) => ({ ...s, id: created.id, titolo: titoloVal }));
    setCreatedThisSession(true);
    await loadSetsFor(modalSet.placement);
    return created.id;
  }, [modalSet, token, loadSetsFor]);

  const openCreate = (placement) => {
    setModalSet({
      id: null,
      placement,
      titolo: "",
      descrizione: "",
      active: true,
      sort_order: 0,
      fornitoriIds: [],
    });
    setItems([]);
    setNewItem({
      custom_title: "",
      custom_price: "",
      share_percent: "",
      custom_image: "",
      custom_badge: "",
      custom_note: "",
    });
    setShowMoreItemFields(false);
    setModalTab("details");
    setCreatedThisSession(false);
    setShowDeleted(false);
    setLibSearch("");
    setLibItems([]);
    setLibIncludeDeleted(false);
    setModalOpen(true);
  };

  const openEdit = (s) => {
    const ids = Array.isArray(s.fornitore_ids)
      ? s.fornitore_ids.map((x) => String(x))
      : [];

    setModalSet({
      id: s.id,
      placement: s.placement,
      titolo: s.titolo || "",
      descrizione: s.descrizione || "",
      active: !!s.active,
      sort_order: Number(s.sort_order || 0),
      fornitoriIds: ids,
    });

    setItems([]);
    setNewItem({
      custom_title: "",
      custom_price: "",
      share_percent: "",
      custom_image: "",
      custom_badge: "",
      custom_note: "",
    });
    setShowMoreItemFields(false);
    setModalTab("details");
    setCreatedThisSession(false);
    setShowDeleted(false);
    setLibSearch("");
    setLibItems([]);
    setLibIncludeDeleted(false);
    setModalOpen(true);
  };

  const closeModal = async () => {
    if (createdThisSession && modalSet.id) {
      try {
        // se non hai item ATTIVI associati, elimino la bozza
        const aliveCount = (items || []).filter((x) => !x.deleted_at).length;
        if (aliveCount === 0) {
          await api("DELETE", `/api/admin/cross-sell/sets/${modalSet.id}`, null, token);
          await loadSetsFor(modalSet.placement);
          showToast({ type: "info", message: "Bozza set eliminata" });
        }
      } catch {
        // niente
      }
    }
    setModalOpen(false);
  };

  const saveModalSet = async (e) => {
    e?.preventDefault?.();

    const titoloVal = (modalSet.titolo || "").trim();
    if (!titoloVal) {
      showToast({ type: "danger", message: "Titolo obbligatorio" });
      return;
    }

    const ids = (modalSet.fornitoriIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);

    const payload = {
      placement: modalSet.placement,
      titolo: titoloVal,
      descrizione: (modalSet.descrizione || "").trim(),
      active: !!modalSet.active,
      sort_order: Number(modalSet.sort_order || 0),
      fornitore_ids: ids,
    };

    try {
      if (modalSet.id) {
        await api("PUT", `/api/admin/cross-sell/sets/${modalSet.id}`, payload, token);
      } else {
        const created = await api("POST", `/api/admin/cross-sell/sets`, payload, token);
        setModalSet((s) => ({ ...s, id: created.id }));
        setCreatedThisSession(true);
      }
      await loadSetsFor(modalSet.placement);
      showToast({ type: "success", message: "Set salvato" });
    } catch (e2) {
      showToast({
        type: "danger",
        message: e2.message || "Errore salvataggio set",
      });
    }
  };

  /* -------------------- Items: load + DnD (reorder) -------------------- */
  const loadItemsForSet = useCallback(
    async (setId) => {
      if (!setId) return setItems([]);
      setLoadingItems(true);

      try {
        const qs = new URLSearchParams();
        if (showDeleted) qs.set("includeDeleted", "1");

        const data = await api(
          "GET",
          `/api/admin/cross-sell/sets/${setId}/items${qs.toString() ? `?${qs}` : ""}`,
          null,
          token
        );

        const sorted = Array.isArray(data) ? sortByOrder(data) : [];
        setItems(sorted);

        const alive = sorted.filter((it) => !it.deleted_at);
        const titles = alive.map((it) => itemTitle(it)).filter(Boolean);

        setItemsMetaMap((prev) => ({
          ...prev,
          [setId]: {
            count: alive.length,
            first3: titles.slice(0, 3),
            full: titles.join(", "),
          },
        }));
      } catch {
        setItems([]);
      } finally {
        setLoadingItems(false);
      }
    },
    [token, sortByOrder, showDeleted]
  );

  // ricarica items quando toggle showDeleted cambia e siamo nel tab items con set valido
  useEffect(() => {
    if (!modalOpen) return;
    if (modalTab !== "items") return;
    if (!modalSet.id) return;
    loadItemsForSet(modalSet.id);
  }, [showDeleted, modalOpen, modalTab, modalSet.id, loadItemsForSet]);

  const loadLibrary = useCallback(
    async (search, includeDel) => {
      if (!modalOpen || modalTab !== "items") return;
      setLibLoading(true);
      try {
        const qs = new URLSearchParams();
        if (search && search.trim()) qs.set("search", search.trim());
        if (includeDel) qs.set("includeDeleted", "1");

        const data = await api(
          "GET",
          `/api/admin/cross-sell/items${qs.toString() ? `?${qs.toString()}` : ""}`,
          null,
          token
        );
        setLibItems(Array.isArray(data) ? data : []);
      } catch {
        setLibItems([]);
      } finally {
        setLibLoading(false);
      }
    },
    [token, modalOpen, modalTab]
  );

  // refresh libreria quando si apre tab items
  useEffect(() => {
    if (!modalOpen) return;
    if (modalTab !== "items") return;
    loadLibrary(libSearch, libIncludeDeleted);
  }, [modalOpen, modalTab, libIncludeDeleted, loadLibrary]); // libSearch è gestito via “Cerca”

  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const onDragStart = (index) => () => setDragIndex(index);
  const onDragEnter = (index) => () => setOverIndex(index);
  const onDragOver = (e) => e.preventDefault();
  const onDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const move = (arr, from, to) => {
    const copy = arr.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };

  const reorderActiveInUI = async (fromIndex, toIndex) => {
    // consenti DnD SOLO tra attivi
    const activeItems = items.filter((it) => !it.deleted_at);
    const deletedItems = items.filter((it) => it.deleted_at);

    const dragged = items[fromIndex];
    const target = items[toIndex];
    if (!dragged || !target) return;

    if (dragged.deleted_at || target.deleted_at) {
      showToast({ type: "info", message: "Riordino disponibile solo per item attivi" });
      return;
    }

    // trovare gli indici nella lista active
    const activeFrom = activeItems.findIndex((x) => x.id === dragged.id);
    const activeTo = activeItems.findIndex((x) => x.id === target.id);
    if (activeFrom < 0 || activeTo < 0) return;

    const reorderedActive = move(activeItems, activeFrom, activeTo);
    const reorderedAll = [...reorderedActive, ...deletedItems];
    setItems(reorderedAll);

    // chiama reorder backend con solo gli attivi
    try {
      await api(
        "PUT",
        `/api/admin/cross-sell/sets/${modalSet.id}/items/reorder`,
        { itemIds: reorderedActive.map((x) => x.id) },
        token
      );
      await loadItemsForSet(modalSet.id);
      showToast({ type: "success", message: "Ordine aggiornato" });
    } catch (e2) {
      showToast({ type: "danger", message: e2.message || "Errore riordino items" });
      await loadItemsForSet(modalSet.id);
    }
  };

  const onDrop = (index) => async (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return onDragEnd();
    const from = dragIndex;
    const to = index;
    setDragIndex(null);
    setOverIndex(null);
    await reorderActiveInUI(from, to);
  };

  /* -------------------- Create item globale + attach al set -------------------- */
  async function addInlineItem(e) {
    e?.preventDefault?.();
    const setId = await ensureSetExists();

    const titoloVal = (newItem.custom_title || "").trim();
    if (!titoloVal) {
      showToast({ type: "danger", message: "Titolo obbligatorio" });
      return;
    }

    const payload = {
      service_id: null,
      custom_title: titoloVal,
      custom_price: !isEmpty(newItem.custom_price) ? Number(newItem.custom_price) : null,
      custom_image: (newItem.custom_image || "").trim() || null,
      custom_badge: (newItem.custom_badge || "").trim() || null,
      custom_note: (newItem.custom_note || "").trim() || null,
      share_percent: clampPercent(newItem.share_percent),
    };

    try {
      const created = await api("POST", `/api/admin/cross-sell/items`, payload, token);

      await api(
        "POST",
        `/api/admin/cross-sell/sets/${setId}/items/${created.id}`,
        null,
        token
      );

      setNewItem({
        custom_title: "",
        custom_price: "",
        share_percent: "",
        custom_image: "",
        custom_badge: "",
        custom_note: "",
      });
      setShowMoreItemFields(false);

      await loadItemsForSet(setId);
      await loadLibrary(libSearch, libIncludeDeleted);
      showToast({ type: "success", message: "Item creato e associato" });
    } catch (e2) {
      showToast({ type: "danger", message: e2.message || "Errore creazione item" });
    }
  }

  /* -------------------- Attach / Detach -------------------- */
  const attachItemToSet = async (itemId) => {
    if (!itemId) return;
    const setId = await ensureSetExists();

    try {
      await api(
        "POST",
        `/api/admin/cross-sell/sets/${setId}/items/${itemId}`,
        null,
        token
      );
      await loadItemsForSet(setId);
      showToast({ type: "success", message: "Item associato al set" });
    } catch (e2) {
      showToast({ type: "danger", message: e2.message || "Errore associazione item" });
    }
  };

  const detachItemFromSet = async (itemId) => {
    if (!itemId || !modalSet.id) return;

    confirmToast(
      `Staccare l’item #${itemId} da questo set?`,
      async () => {
        try {
          await api(
            "DELETE",
            `/api/admin/cross-sell/sets/${modalSet.id}/items/${itemId}`,
            null,
            token
          );
          await loadItemsForSet(modalSet.id);
          showToast({ type: "success", message: "Item staccato dal set" });
        } catch (e2) {
          showToast({ type: "danger", message: e2.message || "Errore detach item" });
        }
      },
      { type: "danger", confirmLabel: "Stacca" }
    );
  };

  /* -------------------- Archivia / Restore globale (opzionale) -------------------- */
  const archiveGlobalItem = async (itemId) => {
    if (!itemId) return;
    confirmToast(
      `Archiviare globalmente l’item #${itemId}? (sparirà ovunque)`,
      async () => {
        try {
          await api("DELETE", `/api/admin/cross-sell/items/${itemId}`, null, token);
          await loadItemsForSet(modalSet.id);
          await loadLibrary(libSearch, libIncludeDeleted);
          showToast({ type: "success", message: "Item archiviato (globale)" });
        } catch (e2) {
          showToast({ type: "danger", message: e2.message || "Errore archiviazione item" });
        }
      },
      { type: "danger", confirmLabel: "Archivia" }
    );
  };

  const restoreGlobalItem = async (itemId) => {
    if (!itemId) return;
    try {
      await api("PATCH", `/api/admin/cross-sell/items/${itemId}/restore`, null, token);
      await loadItemsForSet(modalSet.id);
      await loadLibrary(libSearch, libIncludeDeleted);
      showToast({ type: "success", message: "Item ripristinato (globale)" });
    } catch (e2) {
      showToast({ type: "danger", message: e2.message || "Errore ripristino item" });
    }
  };

  /* -------------------- Edit item (modal piccola) -------------------- */
  const [formItem, setFormItem] = useState({
    id: null,
    custom_title: "",
    custom_price: "",
    custom_image: "",
    custom_badge: "",
    custom_note: "",
    share_percent: "",
  });

  const startEditItem = (it) => {
    setFormItem({
      id: it.id,
      custom_title: (it.custom_title || it.titolo || it.display_title || "").trim(),
      custom_price: !isEmpty(it.custom_price) ? String(it.custom_price) : "",
      custom_image: it.custom_image || "",
      custom_badge: it.custom_badge || "",
      custom_note: it.custom_note || "",
      share_percent:
        it.share_percent !== undefined && it.share_percent !== null
          ? String(it.share_percent)
          : "",
    });
    setItemFormOpen(true);
  };

  const saveEditedItem = async (e) => {
    e?.preventDefault?.();
    if (!formItem.id) return;

    const payload = {
      service_id: null,
      custom_title: (formItem.custom_title || "").trim(),
      custom_price: !isEmpty(formItem.custom_price) ? Number(formItem.custom_price) : null,
      custom_image: (formItem.custom_image || "").trim() || null,
      custom_badge: (formItem.custom_badge || "").trim() || null,
      custom_note: (formItem.custom_note || "").trim() || null,
      share_percent: clampPercent(formItem.share_percent),
    };

    try {
      await api("PUT", `/api/admin/cross-sell/items/${formItem.id}`, payload, token);
      setItemFormOpen(false);
      await loadItemsForSet(modalSet.id);
      await loadLibrary(libSearch, libIncludeDeleted);
      showToast({ type: "success", message: "Item salvato" });
    } catch (e2) {
      showToast({ type: "danger", message: e2.message || "Errore salvataggio item" });
    }
  };

  const renderSupplierCell = (s) => {
    const ids = Array.isArray(s.fornitore_ids) ? s.fornitore_ids.map(String) : [];
    if (ids.length === 0) return "Globale";
    const names = ids.map((id) => supplierNameById.get(id) || `#${id}`);
    return names.join(", ");
  };

  /* -------------------- Section (lista per placement) -------------------- */
  const Section = ({ title, placement, sets, loading, error }) => (
    <section className="cs-card">
      <div className="cs-row">
        <h2 className="cs-h2">{title}</h2>
        <button
          className="cs-btn cs-btn--primary cs-btn--pill"
          onClick={() => openCreate(placement)}
        >
          <span className="cs-btn-emoji" aria-hidden="true">➕</span>
        </button>
      </div>

      {loading && <div>Carico set…</div>}
      {error && <div className="cs-err">{error}</div>}

      <div className="cs-table cs-table--setsV2">
        <div className="cs-thRow">
          <div className="cs-th">ID</div>
          <div className="cs-th">Titolo</div>
          <div className="cs-th">Attivo</div>
          <div className="cs-th">Fornitori</div>
          <div className="cs-th">Item 1</div>
          <div className="cs-th">Item 2</div>
          <div className="cs-th">Item 3</div>
          <div className="cs-th">Azioni</div>
        </div>

        {sortByOrder(sets).map((s) => {
          const meta = itemsMetaMap[s.id] || { first3: [] };
          const [i1, i2, i3] = meta.first3;
          return (
            <div key={s.id} className="cs-tr">
              <div className="cs-td">{s.id}</div>
              <div className="cs-td">{s.titolo}</div>
              <div className="cs-td">{s.active ? "✔" : "—"}</div>
              <div
                className="cs-td cs-td-ellipsis cs-td--supplier"
                title={renderSupplierCell(s)}
              >
                {renderSupplierCell(s)}
              </div>
              <div className="cs-td"><span className="cs-clip" title={i1 || ""}>{i1 || "—"}</span></div>
              <div className="cs-td"><span className="cs-clip" title={i2 || ""}>{i2 || "—"}</span></div>
              <div className="cs-td"><span className="cs-clip" title={i3 || ""}>{i3 || "—"}</span></div>
              <div className="cs-td cs-td-actions">
                <button
                  className="cs-btn cs-btn--sm cs-btn--icon"
                  onClick={() => openEdit(s)}
                  title="Modifica set"
                  aria-label={`Modifica set ${s.titolo || s.id}`}
                >
                  ✏️
                </button>
                <button
                  className="cs-btn cs-btn--sm cs-btn--icon cs-btn--danger"
                  onClick={() => {
                    confirmToast(
                      `Confermi eliminazione del set #${s.id}?`,
                      async () => {
                        try {
                          await api("DELETE", `/api/admin/cross-sell/sets/${s.id}`, null, token);
                          await loadSetsFor(placement);
                          setItemsMetaMap((prev) => {
                            const copy = { ...prev };
                            delete copy[s.id];
                            return copy;
                          });
                          showToast({ type: "success", message: "Set eliminato" });
                        } catch (e2) {
                          showToast({ type: "danger", message: e2.message || "Errore eliminazione set" });
                        }
                      },
                      { type: "danger", confirmLabel: "Elimina" }
                    );
                  }}
                  title="Elimina set"
                  aria-label={`Elimina set ${s.titolo || s.id}`}
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })}

        {sets.length === 0 && <div className="cs-empty">Nessun set.</div>}
      </div>
    </section>
  );

  /* -------------------- render -------------------- */
  if (!isAdmin) {
    return (
      <div className="cs-page">
        <h2 className="cs-h2">Permesso negato</h2>
        <p>Questa pagina è riservata agli amministratori.</p>
        <button className="cs-btn" onClick={() => navigate("/")}>
          Torna alla home
        </button>
      </div>
    );
  }

  return (
    <div className="cs-page">
      <h1 className="cs-h1">Cross-sell (sets &amp; items)</h1>

      <Section
        title="Elenco – Perché non aggiungi…"
        placement="chips"
        sets={setsChips}
        loading={loadingChips}
        error={errorChips}
      />

      <Section
        title="Elenco – Hai dimenticato qualcosa?"
        placement="carousel"
        sets={setsCarousel}
        loading={loadingCarousel}
        error={errorCarousel}
      />

      {/* -------- Modale UNICA: Set + Tabs -------- */}
      {modalOpen && (
        <div
          className="cs-modal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="cs-modal__content" role="document">
            <div className="cs-modal__header">
              <div className="cs-modal__titleWrap">
                <h3 className="cs-h3 cs-modal__title">
                  {editingSet ? "Modifica set" : "Nuovo set"}
                </h3>
                <div className="cs-modal__meta">
                  <span className="badge muted">
                    {modalSet.placement === "chips"
                      ? "Perché non aggiungi…"
                      : "Hai dimenticato qualcosa?"}
                  </span>
                  {editingSet && <span className="badge muted">ID #{modalSet.id}</span>}
                  {modalSet.titolo && (
                    <span className="cs-modal__subtitle">{modalSet.titolo}</span>
                  )}
                </div>
              </div>

              <div className="cs-modal__actions">
                <button
                  type="button"
                  className="cs-btn cs-btn--primary cs-btn--pill"
                  onClick={saveModalSet}
                >
                  <span className="cs-btn-emoji" aria-hidden="true">💾</span>
                </button>
                <button
                  type="button"
                  className="cs-btn cs-btn--ghost cs-btn--icon"
                  onClick={closeModal}
                  title="Chiudi"
                  aria-label="Chiudi"
                >
                  ✖
                </button>
              </div>
            </div>

            <div className="cs-modal__body">
              <div className="cs-tabs">
                <button
                  className={`cs-tab ${modalTab === "details" ? "is-active" : ""}`}
                  onClick={() => setModalTab("details")}
                >
                  Dettagli
                </button>
                <button
                  className={`cs-tab ${modalTab === "items" ? "is-active" : ""}`}
                  onClick={async () => {
                    setModalTab("items");
                    const id = await ensureSetExists();
                    await loadItemsForSet(id);
                    await loadLibrary(libSearch, libIncludeDeleted);
                  }}
                >
                  Items
                </button>
              </div>

              {modalTab === "details" && (
                <form onSubmit={saveModalSet} className="cs-grid2">
                  <label className="cs-label">
                    <span>Attivo</span>
                    <input
                      type="checkbox"
                      className="cs-checkbox"
                      checked={!!modalSet.active}
                      onChange={(e) =>
                        setModalSet((f) => ({ ...f, active: e.target.checked }))
                      }
                    />
                  </label>

                  <label className="cs-label cs-col-span">
                    Titolo
                    <input
                      className="cs-input"
                      value={modalSet.titolo}
                      onChange={(e) =>
                        setModalSet((f) => ({ ...f, titolo: e.target.value }))
                      }
                      required
                    />
                  </label>

                  <label className="cs-label cs-col-span">
                    Descrizione
                    <input
                      className="cs-input"
                      value={modalSet.descrizione}
                      onChange={(e) =>
                        setModalSet((f) => ({ ...f, descrizione: e.target.value }))
                      }
                    />
                  </label>

                  <label className="cs-label cs-col-span">
                    Fornitori (Globale = nessun fornitore specifico)
                    <select
                      className="cs-input cs-input--multiselect"
                      value={modalSet.fornitoriIds.length ? modalSet.fornitoriIds : [""]}
                      onChange={(e) => {
                        const opts = Array.from(e.target.options);
                        let sel = opts.filter((o) => o.selected).map((o) => o.value);

                        if (sel.includes("")) {
                          if (sel.length === 1) sel = [];
                          else sel = sel.filter((v) => v !== "");
                        }

                        setModalSet((f) => ({ ...f, fornitoriIds: sel }));
                      }}
                      multiple
                      size={Math.min(8, Math.max(4, fornitori.length + 1))}
                    >
                      <option value="">🌍 Globale (tutti i fornitori)</option>
                      {fornitori.map((f) => (
                        <option key={f.id} value={String(f.id)}>
                          {f.nome || `Fornitore #${f.id}`}
                        </option>
                      ))}
                    </select>
                    <small className="cs-help">
                      Seleziona uno o più fornitori per limitarlo, oppure “Globale” per
                      applicarlo a tutti.
                    </small>
                  </label>
                </form>
              )}

              {modalTab === "items" && (
                <div className="cs-items">
                  {/* Top bar compatta */}
                  <div className="cs-items__topbar">
                    <div className="cs-items__title">
                      <h4 className="cs-h4">Items</h4>
                      <span className="badge muted">Set #{modalSet.id || "—"}</span>
                    </div>

                    <label className="cs-switch" title="Mostra anche gli item archiviati (globali)">
                      <input
                        type="checkbox"
                        checked={showDeleted}
                        onChange={(e) => setShowDeleted(e.target.checked)}
                      />
                      <span className="cs-switch__track" aria-hidden="true" />
                      <span className="cs-switch__label">Archiviati nel set</span>
                    </label>
                  </div>

                  {/* ITEM HUB: crea + libreria (accorpati, compatti, moderni) */}
                  <div className="cs-hub">
                    <div className="cs-hub__head">
                      <div className="cs-hub__headLeft">
                        <div className="cs-hub__icon" aria-hidden="true">🧩</div>
                        <div>
                          <div className="cs-hub__title">Item Hub</div>
                          <div className="cs-hub__subtitle">
                            Crea nuovi item o associa elementi già esistenti
                          </div>
                        </div>
                      </div>

                      <div className="cs-hub__tabs" role="tablist" aria-label="Item Hub">
                        <button
                          type="button"
                          className={`cs-pill ${itemHubTab === "create" ? "is-active" : ""}`}
                          onClick={() => setItemHubTab("create")}
                          role="tab"
                          aria-selected={itemHubTab === "create"}
                        >
                          Crea
                        </button>
                        <button
                          type="button"
                          className={`cs-pill ${itemHubTab === "library" ? "is-active" : ""}`}
                          onClick={() => setItemHubTab("library")}
                          role="tab"
                          aria-selected={itemHubTab === "library"}
                        >
                          Libreria
                        </button>
                      </div>
                    </div>

                    <div className="cs-hub__body">
                      {/* TAB: CREATE */}
                      {itemHubTab === "create" && (
                        <form onSubmit={addInlineItem} className="cs-hubForm">
                          <div className="cs-hubForm__grid">
                            <label className="cs-field cs-field--span2">
                              <span className="cs-field__label">Titolo *</span>
                              <input
                                className="cs-input cs-input--dense"
                                placeholder="Es. Lavaggio camicia extra"
                                value={newItem.custom_title}
                                onChange={(e) =>
                                  setNewItem((x) => ({ ...x, custom_title: e.target.value }))
                                }
                                required
                              />
                            </label>

                            <label className="cs-field">
                              <span className="cs-field__label">Prezzo</span>
                              <input
                                type="number"
                                step="0.01"
                                className="cs-input cs-input--dense"
                                placeholder="€"
                                value={newItem.custom_price}
                                onChange={(e) =>
                                  setNewItem((x) => ({ ...x, custom_price: e.target.value }))
                                }
                              />
                            </label>
                          </div>

                          {/* toggle avanzate */}
                          <div className="cs-hubForm__row">
                            <button type="submit" className="cs-btn cs-btn--primary cs-btn--dense">
                              Crea + Associa
                            </button>

                            <button
                              type="button"
                              className="cs-btn cs-btn--ghost cs-btn--dense"
                              onClick={() => setShowMoreItemFields((v) => !v)}
                              aria-expanded={showMoreItemFields}
                            >
                              {showMoreItemFields ? "Nascondi avanzate" : "Avanzate"}
                            </button>

                            <button
                              type="button"
                              className="cs-btn cs-btn--ghost cs-btn--dense"
                              onClick={() => {
                                setNewItem({
                                  custom_title: "",
                                  custom_price: "",
                                  share_percent: "",
                                  custom_image: "",
                                  custom_badge: "",
                                  custom_note: "",
                                });
                                setShowMoreItemFields(false);
                              }}
                            >
                              Reset
                            </button>
                          </div>

                          {showMoreItemFields && (
                            <div className="cs-hubForm__grid cs-hubForm__grid--more">
                              <label className="cs-field cs-field--span2">
                                <span className="cs-field__label">Immagine (URL)</span>
                                <input
                                  className="cs-input cs-input--dense"
                                  placeholder="https://..."
                                  value={newItem.custom_image}
                                  onChange={(e) =>
                                    setNewItem((x) => ({ ...x, custom_image: e.target.value }))
                                  }
                                />
                              </label>

                              <label className="cs-field">
                                <span className="cs-field__label">Badge</span>
                                <input
                                  className="cs-input cs-input--dense"
                                  placeholder="Es. Promo"
                                  value={newItem.custom_badge}
                                  onChange={(e) =>
                                    setNewItem((x) => ({ ...x, custom_badge: e.target.value }))
                                  }
                                />
                              </label>

                              <label className="cs-field">
                                <span className="cs-field__label">Nota</span>
                                <input
                                  className="cs-input cs-input--dense"
                                  placeholder="Es. consegna 48h"
                                  value={newItem.custom_note}
                                  onChange={(e) =>
                                    setNewItem((x) => ({ ...x, custom_note: e.target.value }))
                                  }
                                />
                              </label>

                              <label className="cs-field">
                                <span className="cs-field__label">Quota %</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  className="cs-input cs-input--dense"
                                  placeholder="%"
                                  value={newItem.share_percent}
                                  onChange={(e) =>
                                    setNewItem((x) => ({ ...x, share_percent: e.target.value }))
                                  }
                                />
                              </label>
                            </div>
                          )}

                          <div className="cs-hubForm__hint">
                            L’item viene creato nella libreria globale e associato a questo set.
                          </div>
                        </form>
                      )}

                      {/* TAB: LIBRARY */}
                      {itemHubTab === "library" && (
                        <div className="cs-hubLib">
                          <div className="cs-hubLib__controls">
                            <div className="cs-hubLib__search">
                              <input
                                className="cs-input cs-input--dense"
                                placeholder="Cerca item…"
                                value={libSearch}
                                onChange={(e) => setLibSearch(e.target.value)}
                              />
                              <button
                                className="cs-btn cs-btn--primary cs-btn--dense"
                                type="button"
                                onClick={() => loadLibrary(libSearch, libIncludeDeleted)}
                              >
                                Cerca
                              </button>
                            </div>

                            <label className="cs-switch cs-switch--sm" title="Mostra archiviati anche in libreria">
                              <input
                                type="checkbox"
                                checked={libIncludeDeleted}
                                onChange={(e) => setLibIncludeDeleted(e.target.checked)}
                              />
                              <span className="cs-switch__track" aria-hidden="true" />
                              <span className="cs-switch__label">Archiviati</span>
                            </label>
                          </div>

                          {libLoading ? (
                            <div className="cs-muted" style={{ padding: 8 }}>Carico libreria…</div>
                          ) : (
                            <div className="cs-libList">
                              {libItems.map((it) => {
                                const isDeleted = !!it.deleted_at;
                                const inSet = items.some((x) => x.id === it.id);

                                return (
                                  <div key={it.id} className={`cs-libRow ${isDeleted ? "is-deleted" : ""}`}>
                                    <div className="cs-libRow__main">
                                      <div className="cs-libRow__title">
                                        {itemTitle(it)}
                                        <span className="cs-libRow__meta">
                                          #{it.id}
                                          {!isEmpty(it.custom_price) && ` · €${it.custom_price}`}
                                          {!isEmpty(it.share_percent) && ` · ${it.share_percent}%`}
                                        </span>
                                      </div>
                                      {isDeleted && <span className="badge danger">Archiviato</span>}
                                    </div>

                                    <div className="cs-libRow__actions">
                                      {!isDeleted ? (
                                        <button
                                          className={`cs-btn cs-btn--dense ${inSet ? "cs-btn--ghost" : "cs-btn--primary"}`}
                                          type="button"
                                          onClick={() => attachItemToSet(it.id)}
                                          disabled={inSet}
                                          title={inSet ? "Già associato" : "Associa al set"}
                                        >
                                          {inSet ? "Associato" : "Associa"}
                                        </button>
                                      ) : (
                                        <button
                                          className="cs-btn cs-btn--dense cs-btn--ghost"
                                          type="button"
                                          onClick={() => restoreGlobalItem(it.id)}
                                          title="Ripristina item"
                                        >
                                          Ripristina
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                              {libItems.length === 0 && (
                                <div className="cs-empty cs-empty--tight">Nessun item trovato.</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lista items associati al set */}
                  <div className="cs-linked">
                    <div className="cs-linked__head">
                      <div className="cs-linked__title">
                        <h4 className="cs-h4">Associati al set</h4>
                        <span className="cs-muted">
                          Trascina per riordinare (solo attivi)
                        </span>
                      </div>
                    </div>

                    {loadingItems ? (
                      <div className="cs-muted" style={{ padding: 8 }}>Carico items…</div>
                    ) : (
                      <div className="cs-table cs-table--itemsV2 cs-table--dense">
                        <div className="cs-thRow">
                          <div className="cs-th">#</div>
                          <div className="cs-th">Titolo</div>
                          <div className="cs-th">Prezzo</div>
                          <div className="cs-th">%</div>
                          <div className="cs-th">Stato</div>
                          <div className="cs-th">Azioni</div>
                        </div>

                        {items.map((it, i) => {
                          const isDeleted = !!it.deleted_at;
                          const title = itemTitle(it);

                          return (
                            <div
                              key={it.id}
                              className={`cs-tr ${isDeleted ? "is-deleted" : ""} ${i === dragIndex ? "is-dragging" : ""
                                } ${i === overIndex && dragIndex !== null ? "is-over" : ""}`}
                              draggable={!isDeleted}
                              onDragStart={!isDeleted ? onDragStart(i) : undefined}
                              onDragEnter={!isDeleted ? onDragEnter(i) : undefined}
                              onDragOver={!isDeleted ? onDragOver : undefined}
                              onDragEnd={!isDeleted ? onDragEnd : undefined}
                              onDrop={!isDeleted ? onDrop(i) : undefined}
                              title={isDeleted ? "Item archiviato globalmente" : undefined}
                            >
                              <div className="cs-td">
                                <span
                                  className="cs-drag-handle"
                                  title={isDeleted ? "Archiviato (non riordinabile)" : "Trascina per riordinare"}
                                  style={{ opacity: isDeleted ? 0.4 : 1 }}
                                >
                                  ≡
                                </span>
                              </div>

                              <div className="cs-td">{title}</div>

                              <div className="cs-td">
                                {isEmpty(it.custom_price) ? "—" : it.custom_price}
                              </div>

                              <div className="cs-td">
                                {isEmpty(it.share_percent) ? "—" : `${it.share_percent}`}
                              </div>

                              <div className="cs-td">
                                {isDeleted ? "Archiviato" : "Attivo"}
                              </div>

                              <div className="cs-td cs-td-actions">
                                <button
                                  className="cs-btn cs-btn--sm cs-btn--icon"
                                  onClick={() => startEditItem(it)}
                                  title="Modifica item (globale)"
                                  aria-label="Modifica item"
                                >
                                  ✏️
                                </button>

                                <button
                                  className="cs-btn cs-btn--sm cs-btn--icon"
                                  onClick={() => detachItemFromSet(it.id)}
                                  title="Stacca dal set"
                                  aria-label="Stacca dal set"
                                >
                                  📎
                                </button>

                                {!isDeleted ? (
                                  <button
                                    className="cs-btn cs-btn--sm cs-btn--icon cs-btn--danger"
                                    onClick={() => archiveGlobalItem(it.id)}
                                    title="Archivia globalmente (sparisce ovunque)"
                                    aria-label="Archivia globale"
                                  >
                                    🗑️
                                  </button>
                                ) : (
                                  <button
                                    className="cs-btn cs-btn--sm cs-btn--icon"
                                    onClick={() => restoreGlobalItem(it.id)}
                                    title="Ripristina globale"
                                    aria-label="Ripristina globale"
                                  >
                                    ♻️
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {items.length === 0 && <div className="cs-empty cs-empty--tight">Nessun item associato.</div>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -------- Modale Item (edit globale) -------- */}
      {itemFormOpen && (
        <div
          className="cs-modal cs-modal--nested"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setItemFormOpen(false);
          }}
        >
          <div className="cs-modal__content" role="document">
            <div className="cs-modal__header">
              <div className="cs-modal__titleWrap">
                <h3 className="cs-h3 cs-modal__title">
                  {formItem.id ? "Modifica item" : "Nuovo item"}
                </h3>
                {formItem.custom_title && (
                  <div className="cs-modal__subtitle">{formItem.custom_title}</div>
                )}
              </div>

              <button
                className="cs-btn cs-btn--ghost cs-btn--icon"
                onClick={() => setItemFormOpen(false)}
                title="Chiudi"
                aria-label="Chiudi"
              >
                ✕
              </button>
            </div>

            <div className="cs-modal__body">
              <form onSubmit={saveEditedItem} className="cs-grid2">
                <label className="cs-label cs-col-span">
                  Titolo
                  <input
                    className="cs-input"
                    value={formItem.custom_title}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, custom_title: e.target.value }))
                    }
                    required
                  />
                </label>

                <label className="cs-label">
                  Prezzo
                  <input
                    type="number"
                    step="0.01"
                    className="cs-input"
                    value={formItem.custom_price}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, custom_price: e.target.value }))
                    }
                  />
                </label>

                <label className="cs-label">
                  Quota %
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    className="cs-input"
                    value={formItem.share_percent}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, share_percent: e.target.value }))
                    }
                  />
                </label>

                <label className="cs-label cs-col-span">
                  Immagine (URL)
                  <input
                    className="cs-input"
                    value={formItem.custom_image}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, custom_image: e.target.value }))
                    }
                  />
                </label>

                <label className="cs-label">
                  Badge
                  <input
                    className="cs-input"
                    value={formItem.custom_badge}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, custom_badge: e.target.value }))
                    }
                  />
                </label>

                <label className="cs-label">
                  Nota
                  <input
                    className="cs-input"
                    value={formItem.custom_note}
                    onChange={(e) =>
                      setFormItem((f) => ({ ...f, custom_note: e.target.value }))
                    }
                  />
                </label>

                <div className="cs-form-actions cs-col-span">
                  <button type="submit" className="cs-btn cs-btn--primary">
                    Salva item
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn--ghost"
                    onClick={() => setItemFormOpen(false)}
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* -------- TOASTS -------- */}
      <div className="cs-toasts" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div key={t.id} className={`cs-toast cs-toast--${t.type}`}>
            <div className="cs-toast__msg">{t.message}</div>
            <div className="cs-toast__actions">
              {t.actionLabel && (
                <button
                  className="cs-btn cs-btn--sm cs-btn--primary"
                  onClick={() => t.onAction?.()}
                >
                  {t.actionLabel}
                </button>
              )}
              <button
                className="cs-btn cs-btn--sm cs-btn--ghost"
                onClick={() => dismissToast(t.id)}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
